/**
 * PackSettingsBlock — «Управление пачкой», the always-visible card section of the blogger
 * card's «Статистика» tab for the pack picked by the chips (it was a modal dialog, then an
 * inline block behind «⚙ Настройки пачки», and the account switches sat in a separate
 * «Парсинг» list).
 *
 * Two sub-sections: «Пачка» (name, type, status, application, device, monthly quota, tags,
 * the pack parsing switch, the create-round form, delete) and «Аккаунты пачки» (one row per
 * account: parsing switch, platform, handle, replace / remove; then add). Fields sit in a
 * two-column grid under uppercase labels, like the new-contract form in «Документы».
 *
 * No footer: every control writes immediately through its own mutation.
 *
 * Helpers `PLATFORM_LABEL`, `PlatformBadge`, `errorMessage`, `apiErrorCode` and `todayStr`
 * are copies of the tab's module-local ones (the tab imports this file, so importing back
 * would be a cycle).
 */
import { useMemo, useState } from 'react';
import { PACK_STATUS_LABEL, subStatusLabel, subStatusColor } from '../lib/packBadges.js';
import {
  useUpdatePack,
  useBanPackAccount,
  useDeletePackAccount,
  useDeletePack,
  useAddPackAccount,
  useAddPackAccountById,
} from '../api/packs.js';
import { useLeadAccounts } from '../api/leads.js';
import { normalizeHandle as normalizeSocialHandle } from '../utils.js';
import { accountRemovalErrorMessage } from '../lib/accountInUse.js';
import { APPS } from '../constants/apps.js';
import { useCreateRound } from '../api/rounds.js';
import { Badge, Button, Card, Chip, SelectNative, fmtDayShort } from './ds';
import { AccountParsingToggle, PackParsingToggle, PARSED_PLATFORMS } from './PackParsingControls.jsx';

const PLATFORM_LABEL = {
  instagram: 'IG',
  tiktok:    'TT',
  youtube:   'YT',
  facebook:  'FB',
};

function todayStr () {
  return new Date().toISOString().slice(0, 10);
}

/** Map backend create/delete errors to inline RU messages. */
function errorMessage (err, fallback) {
  const code = err?.response?.data?.error;
  if (code === 'open_round_exists') return 'У пачки уже есть открытый или ожидающий раунд.';
  if (code === 'round_not_pending') return 'Раунд уже стартовал — удалить можно только ожидающий.';
  if (code === 'pack_not_active')   return 'Пачка неактивна — раунд создать нельзя.';
  if (err?.response?.status === 422) return 'Неверная дата старта.';
  return fallback;
}

// apiClient-interceptor (api/client.js) разворачивает axios-ошибку в plain
// Error с .data/.status — .response остаётся только у сырых axios-вызовов.
const apiErrorCode = (err) => err?.data?.error ?? err?.response?.data?.error;

/** The platform pill is MetricTable's own (ds.css) — the same pill the tab draws. */
function PlatformBadge ({ platform }) {
  return (
    <span className={`oc-metric-table__platform oc-metric-table__platform--${platform}`}>
      {PLATFORM_LABEL[platform] ?? platform}
    </span>
  );
}

// SCOPE-HANDLES P1: the local copy is gone — `ban-account` now normalises through
// the seam server-side, so the front uses the shared adapter with the platform.
// ── Аккаунты пачки: «Заменить» (D9, D16) + «Убрать» (PACK-UI). The on/off switch
// moved to the tab's account box (SCOPE-PARSE-TOGGLES).

function PackAccountsSection ({ leadId, pack }) {
  const packOn = pack.parsingEnabled !== false;
  const banMut = useBanPackAccount();
  const removeMut = useDeletePackAccount();
  // One row at a time: the mini-form is opened for a single account (D16).
  const [replacingId, setReplacingId] = useState(null);
  const [newHandle, setNewHandle] = useState('');
  // PACK-UI — the row whose removal was refused. The reason renders under THAT row,
  // not under the section: with three accounts in a pack a section-wide line would
  // not say which one is blocked.
  const [removingId, setRemovingId] = useState(null);

  const packId = pack.id;
  const accounts = pack.accounts ?? [];

  const startReplace = (a) => {
    banMut.reset();
    setReplacingId(a.id);
    setNewHandle('');
  };

  const cancelReplace = () => {
    setReplacingId(null);
    setNewHandle('');
  };

  // PACK-UI — «Убрать из пачки». The route deletes the TrackedAccount row outright,
  // so the confirm says so; it is guarded on the server and refuses rather than
  // cascading (see `removeError` below), and on a refusal NOTHING is mutated.
  const removeAccount = (a) => {
    if (!window.confirm(`Убрать @${a.handle} из пачки? Аккаунт будет удалён полностью — это возможно, только если за ним нет постов и истории.`)) return;
    removeMut.reset();
    setRemovingId(a.id);
    removeMut.mutate({ id: packId, leadId, accountId: a.id });
  };

  // The removal contract of `DELETE /packs/:id/accounts/:accountId`. The route runs the
  // shared `deleteExtraAccount` primitive (FIX-AMB-SAVE §2.7), which refuses with
  // 409 `account_in_use` + `details.blockedBy[]` or 503 `metrics_unavailable` — both
  // rendered from the ONE RU map, lib/accountInUse.js, shared with the card's extras
  // save. `account_has_posts` is the route's OWN pre-check, emitted before the guard,
  // so the map does not carry it and it is mapped here.
  const removeError = removeMut.isError
    ? (apiErrorCode(removeMut.error) === 'account_has_posts'
        ? 'У аккаунта есть посты — убрать нельзя (выключи его вместо этого).'
        : accountRemovalErrorMessage(removeMut.error) ?? 'Не удалось убрать аккаунт.')
    : null;

  // The platform is NOT choosable: the endpoint requires `replacement.platform`, and a
  // swap onto another platform is an ADDITION, which the element map leaves in
  // «Инфо → Служебное». So it is taken from the account being replaced.
  const submitReplace = (a) => {
    const handle = normalizeSocialHandle(newHandle, a.platform);
    if (!handle) return;
    if (!window.confirm(`Заменить @${a.handle} на @${handle}? Старый аккаунт будет помечен забаненным и выйдет из текущего раунда — отменить это через интерфейс нельзя.`)) return;
    banMut.mutate(
      { id: packId, leadId, trackedAccountId: a.id, replacement: { platform: a.platform, handle } },
      // The hook's own invalidations refresh the pack; the dialog STAYS OPEN — its
      // account list is the evidence that the change landed (D16).
      { onSuccess: () => { setReplacingId(null); setNewHandle(''); } },
    );
  };

  return (
    <div className="oc-lead-card__psub">
      <div className="oc-lead-card__sect-label">Аккаунты пачки</div>
      {accounts.map((a) => {
        const off = a.monitoringEnabled === false;
        const banned = a.status === 'banned';
        const switchable = PARSED_PLATFORMS.has(a.platform) && (a.status === 'active' || banned);
        return (
          <div key={a.id}>
            <div className={`oc-lead-card__pset-account${banned ? ' oc-lead-card__pset-account--off' : ''}`}>
              {switchable
                ? <AccountParsingToggle leadId={leadId} packId={packId} account={a} packOn={packOn} />
                : <span className="oc-lead-card__acc-toggle-gap" />}
              <PlatformBadge platform={a.platform} /> @{a.handle}
              {banned ? <span className="oc-lead-card__muted"> · бан</span> : !switchable && off && <span className="oc-lead-card__muted"> · выключен</span>}
              {/* «Убрать» is offered on a BANNED row too — a banned account is exactly
                  the junk a manager wants out of the pack; the server guard, not the
                  UI, decides whether it may go. */}
              <span className="oc-lead-card__right">
                {!banned && (
                  <Button
                    hierarchy="tertiary"
                    size="small"
                    disabled={banMut.isPending}
                    onClick={() => startReplace(a)}
                  >Заменить</Button>
                )}
                <Button
                  hierarchy="tertiary"
                  size="small"
                  disabled={removeMut.isPending}
                  onClick={() => removeAccount(a)}
                >Убрать</Button>
              </span>
            </div>
            {removingId === a.id && removeError && (
              <div className="oc-lead-card__error">{removeError}</div>
            )}
            {replacingId === a.id && (
              <div className="oc-lead-card__pset-replace">
                <PlatformBadge platform={a.platform} />
                <input
                  className="form-input oc-lead-card__input"
                  type="text"
                  placeholder="новый аккаунт"
                  value={newHandle}
                  onChange={(e) => setNewHandle(e.target.value)}
                />
                <Button
                  hierarchy="secondary"
                  size="small"
                  disabled={banMut.isPending || !normalizeSocialHandle(newHandle, a.platform)}
                  onClick={() => submitReplace(a)}
                >{banMut.isPending ? 'Меняю…' : 'Заменить'}</Button>
                <Button
                  hierarchy="tertiary"
                  size="small"
                  disabled={banMut.isPending}
                  onClick={cancelReplace}
                >Отмена</Button>
                {banMut.isError && (
                  <div className="oc-lead-card__error">Не удалось заменить аккаунт.</div>
                )}
              </div>
            )}
          </div>
        );
      })}
      {accounts.length === 0 && (
        <span className="oc-lead-card__muted">В пачке нет аккаунтов.</span>
      )}
      <AddPackAccountForm leadId={leadId} pack={pack} />
    </div>
  );
}

/**
 * PACK-UI — «Добавить аккаунт». Two adders behind one disclosure, both on the pack
 * API's existing hooks: `useAddPackAccountById` (`PATCH /packs/:id/add-account`) binds
 * an account the lead ALREADY has — free, or sitting in another pack while it has no
 * metrics (the backend answers 409 `account_has_posts` otherwise) — and
 * `useAddPackAccount` (`POST /packs/:id/accounts`) creates a new platform+handle. The
 * candidate filter is the deleted PackManager's (git 3317342, lines 166-171), unchanged: the lead's non-primary
 * monitoring accounts, not banned, not already in this pack. The backend lowercases and
 * strips the `@` itself (routes/packs.js:663) but stores a pasted URL as-is, so the
 * add-new path runs the shared `normalizeHandle` from `utils.js` (J-1) — managers paste
 * the browser URL — and the button stays disabled while it yields nothing.
 */
function AddPackAccountForm ({ leadId, pack }) {
  const addExistingMut = useAddPackAccountById();
  const addNewMut = useAddPackAccount();
  const { data: accountsEnvelope } = useLeadAccounts(leadId);

  const [open, setOpen] = useState(false);
  const [pickId, setPickId] = useState('');
  const [platform, setPlatform] = useState('instagram');
  const [handle, setHandle] = useState('');

  const picks = useMemo(
    () => (accountsEnvelope?.accounts ?? []).filter(
      (a) => !a.isPrimary
        && a.accountPurpose !== 'blogger'
        && a.status !== 'banned'
        && a.packId !== pack.id,
    ),
    [accountsEnvelope, pack.id],
  );

  const addExisting = () => {
    if (!pickId) return;
    addNewMut.reset();
    addExistingMut.mutate({ id: pack.id, leadId, trackedAccountId: pickId }, { onSuccess: () => setPickId('') });
  };

  const addNew = () => {
    // J-1: managers paste the browser URL, so the shared utils normaliser (all four
    // platforms' URL forms, incl. facebook.com/profile.php?id=, and `@Handle`) turns
    // it into a bare handle; null/empty means nothing addable (the button stays off).
    const h = normalizeSocialHandle(handle, platform);
    if (!h) return;
    addExistingMut.reset();
    addNewMut.mutate({ id: pack.id, leadId, platform, handle: h }, { onSuccess: () => setHandle('') });
  };

  const addExistingError = addExistingMut.isError
    ? (apiErrorCode(addExistingMut.error) === 'account_lead_mismatch' ? 'Аккаунт другого блогера.'
        : apiErrorCode(addExistingMut.error) === 'account_banned' ? 'Аккаунт забанен — добавить нельзя.'
          : apiErrorCode(addExistingMut.error) === 'account_has_posts' ? 'У аккаунта уже есть метрики — между пачками его не переносят. Добавьте новый аккаунт или замените забаненный.'
            : apiErrorCode(addExistingMut.error) === 'metrics_unavailable' ? 'База метрик сейчас недоступна — перенос не проверить. Попробуйте позже.'
              : 'Не удалось переместить аккаунт.')
    : null;
  const addNewError = addNewMut.isError
    ? (apiErrorCode(addNewMut.error) === 'account_already_in_pack' ? 'Этот аккаунт уже в другой пачке.'
        : apiErrorCode(addNewMut.error) === 'account_banned' ? 'Аккаунт забанен — добавить нельзя.'
          : apiErrorCode(addNewMut.error) === 'account_is_primary' ? 'Это основной аккаунт блогера — в пачку идут только амбассадорские.'
            : 'Не удалось добавить аккаунт.')
    : null;

  if (!open) {
    return (
      <div>
        <Button hierarchy="tertiary" size="small" onClick={() => setOpen(true)}>
          + Добавить аккаунт
        </Button>
      </div>
    );
  }

  return (
    <div className="oc-lead-card__pset-add">
      {picks.length > 0 && (
        <div className="oc-lead-card__pset-add-row">
          <SelectNative value={pickId} onChange={(e) => setPickId(e.target.value)}>
            <option value="">— уже заведённый аккаунт —</option>
            {picks.map((a) => (
              <option key={a.id} value={a.id}>
                {PLATFORM_LABEL[a.platform] ?? a.platform} @{a.handle} · {a.packId ? 'из другой пачки' : 'без пачки'}
              </option>
            ))}
          </SelectNative>
          <Button
            hierarchy="secondary"
            size="small"
            disabled={!pickId || addExistingMut.isPending}
            onClick={addExisting}
          >{addExistingMut.isPending ? 'Добавляю…' : 'Добавить'}</Button>
        </div>
      )}
      {addExistingError && <div className="oc-lead-card__error">{addExistingError}</div>}

      <div className="oc-lead-card__pset-add-row">
        <SelectNative value={platform} onChange={(e) => setPlatform(e.target.value)}>
          {Object.entries(PLATFORM_LABEL).map(([value, label]) => (
            <option key={value} value={value}>{label}</option>
          ))}
        </SelectNative>
        <input
          className="form-input oc-lead-card__input"
          type="text"
          placeholder="новый аккаунт"
          value={handle}
          onChange={(e) => setHandle(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') addNew(); }}
        />
        <Button
          hierarchy="secondary"
          size="small"
          disabled={addNewMut.isPending || !normalizeSocialHandle(handle, platform)}
          onClick={addNew}
        >{addNewMut.isPending ? 'Добавляю…' : 'Добавить'}</Button>
        <Button
          hierarchy="tertiary"
          size="small"
          onClick={() => { setOpen(false); setPickId(''); setHandle(''); }}
        >Отмена</Button>
      </div>
      {addNewError && <div className="oc-lead-card__error">{addNewError}</div>}
    </div>
  );
}

/**
 * PACK-UI — the pack's NAME. `useUpdatePack` is the very hook the type / status /
 * application fields write through, so the rename adds no new endpoint. A free-text field
 * has no change event that means «done», so it carries an explicit «Сохранить».
 */
function PackLabelField ({ leadId, pack }) {
  const labelMut = useUpdatePack();
  const [draft, setDraft] = useState(pack.label ?? '');
  const label = draft.trim();
  const save = () => {
    if (!label || label === pack.label) return;
    labelMut.mutate({ id: pack.id, leadId, label });
  };

  return (
    <div className="oc-lead-card__pfield oc-lead-card__pfield--full">
      <span className="oc-lead-card__label">Название</span>
      <div className="oc-lead-card__pinline">
        <input
          className="form-input oc-lead-card__input"
          type="text"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') save(); }}
        />
        <Button
          hierarchy="secondary"
          size="small"
          disabled={labelMut.isPending || !label || label === pack.label}
          onClick={save}
        >{labelMut.isPending ? 'Сохраняю…' : 'Сохранить'}</Button>
      </div>
      {labelMut.isError && <span className="oc-lead-card__error">Не удалось переименовать пачку.</span>}
    </div>
  );
}

// ── The round slot: create form (D6) ────────────────────────────────────────

function CreateRoundForm ({ pack, nextNumber }) {
  const [targetPosts, setTargetPosts] = useState(15);
  const [startDate, setStartDate] = useState(todayStr());
  // PSMO D4 — the date-less start. The two shapes are mutually exclusive (the
  // backend 422s on both together), so the chip disables the date input and the
  // POST carries `startMode` INSTEAD of `startScheduledAt`.
  const [firstPost, setFirstPost] = useState(false);
  const createMut = useCreateRound();

  const accountsCount = pack?.accounts?.length ?? 0;
  const isFuture = startDate > todayStr();
  const isPast = startDate < todayStr();
  const disabled = !pack || accountsCount === 0 || createMut.isPending;

  const handleCreate = () => {
    createMut.mutate({
      packId: pack.id,
      targetPosts,
      ...(firstPost
        ? { startMode: 'first_post' }
        : (startDate ? { startScheduledAt: new Date(`${startDate}T00:00:00`).toISOString() } : {})),
    });
  };

  return (
    <div className="oc-lead-card__pround">
      <div className="oc-lead-card__label">Новое тестирование</div>
      <div className="oc-lead-card__form-row">
        <div>
          <div className="oc-lead-card__label">Цель постов / аккаунт</div>
          <div className="oc-lead-card__chips">
            {[15, 30].map((n) => (
              <Chip
                key={n}
                active={targetPosts === n}
                onClick={() => setTargetPosts(n)}
              >{n}</Chip>
            ))}
          </div>
        </div>
        <div>
          <div className="oc-lead-card__label">Дата начала теста</div>
          <input
            className="form-input oc-lead-card__input"
            type="date"
            value={startDate}
            disabled={firstPost}
            onChange={(e) => setStartDate(e.target.value)}
          />
          <div className="oc-lead-card__chips">
            <Chip active={firstPost} onClick={() => setFirstPost((v) => !v)}>
              Старт по первому видео
            </Chip>
          </div>
        </div>
      </div>
      <div className="oc-lead-card__hint">
        {firstPost
          ? 'Раунд будет создан в статусе «ожидание» и стартует с первым новым видео блогера.'
          : isFuture
            ? `Раунд будет создан в статусе «ожидание» и стартует автоматически ${fmtDayShort(startDate)}.`
            : isPast
              ? `Раунд стартует задним числом с ${fmtDayShort(startDate)} — посты и просмотры посчитаются с этой даты.`
              : 'Раунд стартует сразу.'}
        {' '}Слежение закончится при наборе {targetPosts} постов на каждом аккаунте.
      </div>
      {accountsCount === 0 && pack && (
        <div className="oc-lead-card__muted">Добавь аккаунты в пачку, чтобы запустить раунд.</div>
      )}
      <div>
        <Button hierarchy="primary" disabled={disabled} onClick={handleCreate}>
          {createMut.isPending ? 'Создаю…' : `Создать раунд R${nextNumber}`}
        </Button>
      </div>
      {createMut.isError && (
        <div className="oc-lead-card__error">
          {errorMessage(createMut.error, 'Не удалось создать раунд.')}
        </div>
      )}
    </div>
  );
}

// ── Device, monthly video quota and tags (display metadata only) ────────────

function PackDeviceField ({ leadId, pack }) {
  const deviceMut = useUpdatePack();
  return (
    <div className="oc-lead-card__pfield">
      <span className="oc-lead-card__label">Устройство</span>
      <SelectNative
        value={pack.device ?? ''}
        disabled={deviceMut.isPending}
        onChange={(e) => deviceMut.mutate({ id: pack.id, leadId, device: e.target.value || null })}
      >
        <option value="">— не задано —</option>
        <option value="ios">iOS</option>
        <option value="android">Android</option>
      </SelectNative>
      {deviceMut.isError && <span className="oc-lead-card__error">Не удалось сохранить устройство.</span>}
    </div>
  );
}

function PackQuotaField ({ leadId, pack }) {
  const quotaMut = useUpdatePack();
  const [quotaDraft, setQuotaDraft] = useState(pack.monthlyVideoQuota == null ? '' : String(pack.monthlyVideoQuota));
  const saveQuota = (value) => quotaMut.mutate(
    { id: pack.id, leadId, monthlyVideoQuota: value },
    { onSuccess: (data) => setQuotaDraft(data?.pack?.monthlyVideoQuota == null ? '' : String(data.pack.monthlyVideoQuota)) },
  );
  return (
    <div className="oc-lead-card__pfield oc-lead-card__pfield--full">
      <span className="oc-lead-card__label">Квота видео в месяц</span>
      <div className="oc-lead-card__pinline">
        <input
          className="form-input oc-lead-card__input"
          type="number"
          min="1"
          max="1000"
          value={quotaDraft}
          onChange={(e) => setQuotaDraft(e.target.value)}
        />
        <Button
          hierarchy="secondary"
          size="small"
          disabled={quotaMut.isPending || quotaDraft === ''}
          onClick={() => saveQuota(Number(quotaDraft))}
        >Сохранить</Button>
        <Button
          hierarchy="tertiary"
          size="small"
          disabled={quotaMut.isPending || pack.monthlyVideoQuota == null}
          onClick={() => saveQuota(null)}
        >Очистить</Button>
      </div>
      {quotaMut.isError && <span className="oc-lead-card__error">Не удалось сохранить квоту.</span>}
    </div>
  );
}

function PackTagsField ({ leadId, pack }) {
  const tagsMut = useUpdatePack();
  const [tagDraft, setTagDraft] = useState('');
  const tags = pack.tags ?? [];
  const saveTags = (next, onSuccess) => tagsMut.mutate({ id: pack.id, leadId, tags: next }, { onSuccess });
  const addTag = () => {
    const t = tagDraft.trim();
    if (!t) return;
    saveTags([...tags, t], () => setTagDraft(''));
  };
  return (
    <div className="oc-lead-card__pfield oc-lead-card__pfield--full">
      <span className="oc-lead-card__label">Метки</span>
      <div className="oc-lead-card__pinline">
        <input
          className="form-input oc-lead-card__input"
          type="text"
          maxLength={40}
          value={tagDraft}
          placeholder="Новая метка"
          onChange={(e) => setTagDraft(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') addTag(); }}
        />
        <Button hierarchy="secondary" size="small" disabled={tagsMut.isPending || !tagDraft.trim()} onClick={addTag}>Добавить</Button>
      </div>
      {tags.length > 0 && (
        <span className="oc-lead-card__pset-tags">
          {tags.map((t) => (
            <Badge key={t} color="neutral" type="contained">
              {t}
              <button type="button" className="oc-lead-card__pset-tag-x" aria-label={`Убрать метку ${t}`} disabled={tagsMut.isPending} onClick={() => saveTags(tags.filter((x) => x !== t))}>✕</button>
            </Badge>
          ))}
        </span>
      )}
      {tagsMut.isError && <span className="oc-lead-card__error">Не удалось сохранить метки.</span>}
    </div>
  );
}

// ── The section ─────────────────────────────────────────────────────────────

export function PackSettingsBlock ({ leadId, pack, rounds, nextNumber, leadApplication = null }) {
  const typeMut = useUpdatePack();
  const statusMut = useUpdatePack();
  const appMut = useUpdatePack();
  const deleteMut = useDeletePack();

  if (!pack) return null;

  const packId = pack.id;
  const subStatus = subStatusLabel(pack.subStatus);
  // D6's three-way predicate: an open OR pending round keeps the create form off the
  // section. Offering «Создать раунд» during an open round would be a live button that
  // always errors (POST answers 409 open_round_exists).
  const blockedByRound = (rounds ?? []).find((r) => r.status === 'open' || r.status === 'pending') ?? null;
  const isAmbassador = pack.type === 'ambassador';
  // «Удалить пачку» is offered only on the server's confirmed `deletable` (the DELETE rule itself).
  const isEmptyPack = pack.deletable === true;
  const deleteError = deleteMut.isError
    ? ({
        pack_has_accounts: 'В пачке есть аккаунты — удалить нельзя.',
        pack_has_rounds: 'У пачки есть раунды — удалить нельзя.',
        pack_has_invoices: 'К пачке привязаны инвойсы — удалить нельзя.',
      })[apiErrorCode(deleteMut.error)] ?? 'Не удалось удалить пачку.'
    : null;
  const deletePack = () => {
    if (!window.confirm('Удалить пустую пачку? Это нельзя отменить.')) return;
    deleteMut.reset();
    deleteMut.mutate({ id: packId, leadId });
  };

  const handleStatusChange = (e) => {
    const next = e.target.value;
    statusMut.mutate({ id: packId, leadId, status: next });
  };

  return (
    <Card className="oc-lead-card__section oc-lead-card__pmgmt">
      <div className="oc-lead-card__sect-label">Управление пачкой</div>

      <div className="oc-lead-card__psub">
        <div className="oc-lead-card__sect-label">Пачка</div>
        <div className="oc-lead-card__pgrid">
          <PackLabelField key={pack.id} leadId={leadId} pack={pack} />

          {/* PTML D8 — the ONE ongoing writer of `Pack.type`: manual, here. There is no
              auto-flip at any lead-status writer (owner ruling), so a blogger promoted to
              ambassador through Airtable keeps a `test` pack until a manager flips it — the
              discrepancy report is what makes that visible. Element map §5 renames the
              reference's «Тип раунда» to «Тип пачки»: the control writes `Pack.type`. */}
          <div className="oc-lead-card__pfield">
            <span className="oc-lead-card__label">Тип пачки</span>
            <SelectNative
              value={pack.type ?? 'test'}
              disabled={typeMut.isPending}
              onChange={(e) => typeMut.mutate({ id: packId, leadId, type: e.target.value })}
            >
              <option value="test">Тестовая</option>
              <option value="ambassador">Амбассадорская</option>
            </SelectNative>
            {typeMut.isError && <span className="oc-lead-card__error">Не удалось сохранить тип пачки.</span>}
          </div>

          {/* PSMO §1 — the FOURTH register: «собирается ли эта пачка». It is the only
              writer of `Pack.status` in the UI. `retiredAt` — «off since», NULL exactly
              when the pack is active (D2) — says since when. */}
          <div className="oc-lead-card__pfield">
            <span className="oc-lead-card__label">Статус пачки</span>
            <SelectNative
              value={pack.status ?? 'active'}
              disabled={statusMut.isPending}
              onChange={handleStatusChange}
            >
              <option value="active">{PACK_STATUS_LABEL.active}</option>
              <option value="inactive">{PACK_STATUS_LABEL.inactive}</option>
              <option value="failed">{PACK_STATUS_LABEL.failed}</option>
            </SelectNative>
            {(pack.retiredAt != null || (pack.subStatus === 'stopped' && subStatus)) && (
              <span className="oc-lead-card__pnote">
                {pack.subStatus === 'stopped' && subStatus && (
                  <Badge color={subStatusColor(pack.subStatus)}>{subStatus}</Badge>
                )}
                {pack.retiredAt != null && (
                  <span className="oc-lead-card__muted">выключена с {fmtDayShort(pack.retiredAt)}</span>
                )}
              </span>
            )}
            {statusMut.isError && <span className="oc-lead-card__error">Не удалось сохранить статус пачки.</span>}
          </div>

          {/* P-E4 D6 — the ONE writer of `Pack.application`: one app per pack (Q3, no
              multiselect), the five values of constants/apps.js. The hint prints the
              blogger's own field read-only while the pack is unset (D5), without a
              prefill (D4: no backfill).
              PACK-APP-LOCK (owner ruling 2026-09-30): changed, never cleared — no empty option;
              a NULL pack shows a disabled placeholder that cannot be picked back (PATCH answers
              '' / null with 400). */}
          <div className="oc-lead-card__pfield">
            <span className="oc-lead-card__label">Приложение</span>
            <SelectNative
              value={pack.application ?? ''}
              disabled={appMut.isPending}
              onChange={(e) => appMut.mutate({ id: packId, leadId, application: e.target.value })}
            >
              {pack.application == null && <option value="" disabled>— выберите приложение —</option>}
              {APPS.map((a) => (<option key={a.value} value={a.value}>{a.value}</option>))}
            </SelectNative>
            {leadApplication && pack.application == null && (
              <span className="oc-lead-card__muted">в карточке блогера: {leadApplication}</span>
            )}
            {appMut.isError && <span className="oc-lead-card__error">Не удалось сохранить приложение.</span>}
          </div>

          <PackDeviceField key={`d${pack.id}`} leadId={leadId} pack={pack} />
          <PackQuotaField key={`q${pack.id}`} leadId={leadId} pack={pack} />
          <PackTagsField key={`t${pack.id}`} leadId={leadId} pack={pack} />
        </div>

        <PackParsingToggle leadId={leadId} pack={pack} />

        {!isAmbassador && (blockedByRound ? (
          <div className="oc-lead-card__muted">
            Новый раунд можно создать после закрытия R{blockedByRound.roundNumber}.
          </div>
        ) : (
          <CreateRoundForm pack={pack} nextNumber={nextNumber} />
        ))}

        {isEmptyPack && (
          <div className="oc-lead-card__pset-row">
            <Button hierarchy="tertiary" size="small" disabled={deleteMut.isPending} onClick={deletePack}>Удалить пачку</Button>
            {deleteError && <span className="oc-lead-card__error">{deleteError}</span>}
          </div>
        )}
      </div>

      <PackAccountsSection key={pack.id} leadId={leadId} pack={pack} />
    </Card>
  );
}

export default PackSettingsBlock;
